import React from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import moment from 'moment'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { Link, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { AdminUITheme } from 'types/AdminUiThemeMui'

type PRMActivityNoteCardProps = {
  Icon: React.FC
  content?: string
  titleEventName?: string
  titleProjectName?: string
  titleToolType?: string
  titleToolName?: string
  createdAt?: Date | string
  toolLink?: string
  trackableName?: string
  trackableType?: string
  projectName?: string
  eventType?: string
  isNote?: boolean
}

export const doesProjectPhraseContainsPreposition = (eventType: string) => {
  return ['quickPollResponseUpdated', 'markerVoteDeleted'].includes(eventType)
}

const PRMActivityNoteCard = (props: PRMActivityNoteCardProps) => {
  const { t } = useTranslation()
  const {
    content,
    eventType,
    titleEventName,
    titleToolName,
    titleToolType,
    titleProjectName,
    createdAt,
    Icon,
    toolLink,
    trackableName,
    trackableType,
    projectName,
    isNote = false,
  } = props
  const theme: AdminUITheme = useTheme()
  const DATE_FORMAT = 'D MMMM YYYY, HH:mm'
  return (
    <>
      {
        <Card style={theme.ParticipantGrid.activityNotesBG}>
          <CardContent>
            <Grid container spacing={0.2}>
              <Grid item xs={1}>
                <Icon />
              </Grid>
              <Grid item xs={11}>
                <Box
                  style={theme.ParticipantGrid.prmActivityNoteCard}
                  data-testid="prmActivityNoteCard-header"
                >
                  {!isNote && (
                    <>
                      <Typography
                        style={theme.ParticipantGrid.fontSize16px}
                        color="text.primary"
                        data-testid="prmActivityNoteCard-header-titleEventName"
                      >
                        {t(`${titleEventName}`)}
                      </Typography>
                      <Link
                        href={toolLink}
                        underline="hover"
                        style={theme.ParticipantGrid.prmActivityNoteCard}
                      >
                        {/* HTML to display trackable name related part in the activity */}
                        {trackableName && (
                          <>
                            &nbsp;
                            <Typography
                              style={theme.ParticipantGrid.fontSize16px}
                              color="primary.main"
                              data-testid="prmActivityNoteCard-header-trackableName"
                            >
                              {t(`${titleToolName}`, {
                                toolName: trackableName,
                              })}
                            </Typography>
                          </>
                        )}
                        {/* HTML to display trackable type related part in the activity */}
                        {trackableType && titleToolType && (
                          <>
                            &nbsp;
                            <Typography
                              style={theme.ParticipantGrid.fontSize16px}
                              color="primary.main"
                              data-testid="prmActivityNoteCard-header-titleToolType"
                            >
                              {t(`${titleToolType}`, {
                                toolType: trackableType,
                              })}
                            </Typography>
                          </>
                        )}
                        {/* HTML to display project related part in the activity */}
                        {projectName && (
                          <>
                            {eventType &&
                              doesProjectPhraseContainsPreposition(
                                eventType,
                              ) && <>&nbsp;</>}
                            {t(`${titleProjectName}`, {
                              projectName: projectName,
                            })}
                          </>
                        )}
                      </Link>
                    </>
                  )}
                  {isNote && (
                    <>
                      {t(`${titleEventName}`) && (
                        <Typography
                          style={theme.ParticipantGrid.fontSize16px}
                          color="text.primary"
                          data-testid="prmActivityNoteCard-header-titleEventName"
                        >
                          {trackableName}
                        </Typography>
                      )}
                      {projectName &&
                        t(`${titleProjectName}`, {
                          projectName: projectName,
                        }) &&
                        t(`${titleProjectName}`, {
                          projectName: projectName,
                        }) !== ' ' && (
                          <>
                            &nbsp;
                            <Typography
                              style={theme.ParticipantGrid.fontSize16px}
                              color="text.primary"
                            >
                              {t(`participant.viewNotes.on`)}
                            </Typography>
                            &nbsp;
                            <Typography
                              style={theme.ParticipantGrid.fontSize16px}
                              color="primary.main"
                              data-testid="prmActivityNoteCard-header-titleProjectName"
                            >
                              {projectName}
                            </Typography>
                          </>
                        )}
                    </>
                  )}
                </Box>
                {createdAt && (
                  <Typography
                    style={theme.ParticipantGrid.fontSize12px}
                    color="text.secondary"
                    data-testid="prmActivityNoteCard-date"
                  >
                    {moment(createdAt).format(DATE_FORMAT)}
                  </Typography>
                )}
                {content && (
                  <Typography
                    style={theme.ParticipantGrid.fontSize16px}
                    color="text.primary"
                    data-testid="prmActivityNoteCard-content"
                  >
                    {content}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      }
    </>
  )
}

export default PRMActivityNoteCard
